export type SourceOfIncomeCode =
  | 'monthly_rental'
  | 'social_security'
  | 'retirement_401k'
  | 'total_asset'
  | 'household_contributor'
  | 'other'
  | 'none_of_above';

export type RecommendationDocumentKey =
  | 'recommandationHeadingText'
  | 'recommandationHeadingTextForSelfEmployed'
  | 'quarterlyStatement'
  | 'paystubs'
  | 'paystubsTooltip'
  | 'taxReturns'
  | 'taxReturnTooltip'
  | 'awardLetter'
  | 'rentalLease'
  | 'form1099';

export interface IncomeGuidanceRow {
  key: string;
  title?: string;
  description?: string;
  bullets?: string[];
}

export const SOURCE_OF_INCOME = {
  MONTHLY_RENTAL: 'monthly_rental',
  SOCIAL_SECURITY: 'social_security',
  RETIREMENT_401K: 'retirement_401k',
  TOTAL_ASSET: 'total_asset',
  OTHER: 'other',
  HOUSEHOLD_CONTRIBUTOR: 'household_contributor',
  NONE_OF_ABOVE: 'none_of_above',
} as const;

export const EMPLOYMENT_STATUS = {
  EMPLOYED: 'EMPLOYED',
  SELF_EMPLOYED: 'SELF_EMPLOYED',
  MILITARY: 'MILITARY',
  NOT_EMPLOYED: 'NOT_EMPLOYED',
  RETIRED: 'RETIRED_WITH_INCOME',
} as const;

export const CONSULTING_BUSINESS_CODE = 'CONSULTING';

export const INCOME_RECOMMENDATION_TOOLTIPS: ReadonlyArray<{
  key: RecommendationDocumentKey;
  text: string;
}> = [
  {
    key: 'paystubs',
    text: 'We attempt automatic verification (only available for some employers), and you may use the Secure Upload to expedite.',
  },
  {
    key: 'taxReturns',
    text: 'Most recent within the last 2 years',
  },
];

const HEADING_KEYS = new Set<RecommendationDocumentKey>([
  'recommandationHeadingText',
  'recommandationHeadingTextForSelfEmployed',
]);

const LIST_ITEM_KEYS = new Set<RecommendationDocumentKey>([
  'paystubs',
  'taxReturns',
  'form1099',
  'rentalLease',
  'awardLetter',
]);

export const INCOME_RECOMMENDATION_COPY: Partial<Record<RecommendationDocumentKey, string>> = {
  recommandationHeadingText:
    '2 months of bank statements showing income deposits. If deposits are not available, upload other documents, such as:',
  recommandationHeadingTextForSelfEmployed:
    '3 months of bank statements showing income deposits. If deposits are not available, upload other documents, such as:',
  paystubs: '2 consecutive paystubs, or W2',
  taxReturns: 'Tax return and extensions filed (if applicable)',
  rentalLease: 'Rental lease',
  form1099: 'Form 1099',
  awardLetter: 'Award letter',
  quarterlyStatement:
    'Quarterly statement showing balances from stocks, mutual funds, savings or similar accounts',
};

export const BANK_STATEMENTS_GUIDANCE_TITLE = 'Bank Statements';

export function buildIncomeGuidanceRows(
  keys: RecommendationDocumentKey[] = []
): IncomeGuidanceRow[] {
  const rows: IncomeGuidanceRow[] = [];
  const headingKey = keys.find((key) => HEADING_KEYS.has(key));
  const bullets = keys
    .filter((key) => LIST_ITEM_KEYS.has(key))
    .map((key) => INCOME_RECOMMENDATION_COPY[key])
    .filter((text): text is string => !!text);

  if (headingKey) {
    rows.push({
      key: headingKey,
      title: BANK_STATEMENTS_GUIDANCE_TITLE,
      description: INCOME_RECOMMENDATION_COPY[headingKey],
      bullets: bullets.length ? bullets : undefined,
    });
  } else if (bullets.length) {
    rows.push({
      key: 'recommendation-bullets',
      bullets,
    });
  }

  if (keys.includes('quarterlyStatement')) {
    rows.push({
      key: 'quarterlyStatement',
      description: INCOME_RECOMMENDATION_COPY.quarterlyStatement,
    });
  }

  return rows;
}

/**
 * Shared Apply + Secure Site income-document recommendation keys.
 * Heading/tooltip keys are display-only and are not upload document types.
 */
export function buildRecommendationDocumentKeys(
  employmentStatus: string | null,
  businessType: string | null,
  sourcesOfIncome: string[]
): RecommendationDocumentKey[] {
  const recommendationDocumentKeys: RecommendationDocumentKey[] = [];
  const addRecommendationKeys = (...keys: RecommendationDocumentKey[]): void => {
    keys.forEach((key) => {
      if (!recommendationDocumentKeys.includes(key)) {
        recommendationDocumentKeys.push(key);
      }
    });
  };

  const incomeSourceCodes = new Set(
    (sourcesOfIncome || []).filter((source): source is string => typeof source === 'string')
  );
  const normalizedEmploymentStatus = (employmentStatus || '').toUpperCase();
  const normalizedBusinessType = (businessType || '').toUpperCase();

  const hasMonthlyRental = incomeSourceCodes.has(SOURCE_OF_INCOME.MONTHLY_RENTAL);
  const hasTotalAsset = incomeSourceCodes.has(SOURCE_OF_INCOME.TOTAL_ASSET);
  const hasSocialSecurity = incomeSourceCodes.has(SOURCE_OF_INCOME.SOCIAL_SECURITY);
  const hasRetirement401k = incomeSourceCodes.has(SOURCE_OF_INCOME.RETIREMENT_401K);
  const hasOther = incomeSourceCodes.has(SOURCE_OF_INCOME.OTHER);
  const hasNoneOfAbove = incomeSourceCodes.has(SOURCE_OF_INCOME.NONE_OF_ABOVE);
  const hasOnlyOtherOrNoneOfAbove = hasOnlyOtherOrNoneOfAboveIncomeSources(incomeSourceCodes);
  const hasHouseholdContributor = incomeSourceCodes.has(SOURCE_OF_INCOME.HOUSEHOLD_CONTRIBUTOR);

  switch (normalizedEmploymentStatus) {
    case EMPLOYMENT_STATUS.SELF_EMPLOYED: {
      addRecommendationKeys('recommandationHeadingTextForSelfEmployed');

      if (normalizedBusinessType === CONSULTING_BUSINESS_CODE) {
        addRecommendationKeys('form1099');
      } else {
        addRecommendationKeys('taxReturns', 'taxReturnTooltip');
      }

      if (hasMonthlyRental) {
        addRecommendationKeys('rentalLease');
      }
      break;
    }

    case EMPLOYMENT_STATUS.EMPLOYED: {
      addRecommendationKeys('recommandationHeadingText', 'paystubs', 'paystubsTooltip');
      if (hasMonthlyRental) {
        addRecommendationKeys('rentalLease');
      }
      break;
    }

    case EMPLOYMENT_STATUS.MILITARY: {
      addRecommendationKeys(
        'recommandationHeadingText',
        'paystubs',
        'taxReturns',
        'taxReturnTooltip'
      );
      if (hasMonthlyRental) {
        addRecommendationKeys('rentalLease');
      }
      break;
    }

    case EMPLOYMENT_STATUS.RETIRED:
    case EMPLOYMENT_STATUS.NOT_EMPLOYED: {
      const hasHouseholdOrOtherIncome = hasHouseholdContributor || hasOther || hasNoneOfAbove;
      const hasSocialSecurityOrRetirement = hasSocialSecurity || hasRetirement401k;
      const hasIncomeRequiringAwardOrLease = hasSocialSecurityOrRetirement || hasMonthlyRental;
      const hasOnlyTotalAssetIncome =
        hasTotalAsset &&
        !hasSocialSecurity &&
        !hasRetirement401k &&
        !hasMonthlyRental &&
        !hasHouseholdContributor &&
        !hasOther;
      if (!hasOnlyTotalAssetIncome) {
        addRecommendationKeys('recommandationHeadingText');
      }
      if (
        hasOnlyOtherOrNoneOfAbove ||
        (hasHouseholdOrOtherIncome && !hasIncomeRequiringAwardOrLease)
      ) {
        addRecommendationKeys('taxReturns', 'taxReturnTooltip');
      } else {
        if (hasSocialSecurityOrRetirement) {
          addRecommendationKeys('awardLetter');
        }

        if (hasMonthlyRental) {
          addRecommendationKeys('rentalLease');
        }
      }
      break;
    }

    default: {
      addRecommendationKeys('recommandationHeadingText');

      if (hasSocialSecurity || hasRetirement401k) {
        addRecommendationKeys('awardLetter');
      }

      if (hasMonthlyRental) {
        addRecommendationKeys('rentalLease');
      }
      break;
    }
  }

  if (hasTotalAsset) {
    addRecommendationKeys('quarterlyStatement');
  }

  return recommendationDocumentKeys;
}

export function hasOnlyOtherOrNoneOfAboveIncomeSources(incomeSourceCodes: Set<string>): boolean {
  if (incomeSourceCodes.size === 0) {
    return false;
  }

  const allowedIncomeSources = new Set<SourceOfIncomeCode>([
    SOURCE_OF_INCOME.OTHER,
    SOURCE_OF_INCOME.NONE_OF_ABOVE,
  ]);

  for (const incomeSourceCode of incomeSourceCodes) {
    if (!allowedIncomeSources.has(incomeSourceCode as SourceOfIncomeCode)) {
      return false;
    }
  }

  return true;
}
